import { ok, err, Result } from 'neverthrow'
import {
    ContainerDataType,
    ContainerType,
    AssignmentAtStepType,
    AssignmentScopeType,
    CreateSubmissionType,
    UserTaskFeedback,
    UserJobHistory,
    UserProjectHistoryResponseDTO,
    ClientUserProjectHistoryResponseDTO,
    ClientUserJobHistoryResponseDTO,
    ClientJobTasksOutputResponseDTO,
    GetOnHoldTaskDetailsResponseDTO,
    OnHoldAssignmentScopeResponseType,
    StepLevelTaskDetailsType,
    SubmissionPlayTurnAPIResponseType,
    JobHierarchy,
    StepType,
    ConversationMainInfoType,
    ConversationalTurnTypes,
    Conversationsv2MainInfoType,
    Conversationalv2TurnTypes,
    ToolCallResponseType,
    SubmitTurnDataPayload,
} from 'Types/Tasks.types'
import { AxiosError } from 'axios'
import type { CSATFeedbackData } from 'Types/CSAT.types'
import { SupportedPluginsKeyType } from 'Core/PluginCore/PluginsManager'
import { sendAnalyticsTrack } from 'Utils/analytics.utils.ts'
import { ExpertsClient } from './axios-clients/experts.client'
import { LuigiClient } from './axios-clients/luigi.client'
import { appConfig } from 'Config/app-config'
import { getBearerToken } from 'shared/auth/auth.utils'
import { FileUploadApiResponseType, GetFileDownloadUrlResponseDto } from 'Types/Attribute.types'
import { S3Client } from 'HTTPClients/axios-clients/s3.client'
import { ErrorCode } from 'Constants/constants'
import { ErrorResponseDTO, JSONObject } from 'Types/Common.types'

type GetTaskLevelPluginConfigType = {
    task_level_plugin_configs: {
        id: string
        plugin_id: string
        step_plugin_association_id: string
        task_id: string
        enabled: boolean
        usage_stats: number
        type: SupportedPluginsKeyType
        config: Record<string, unknown>
    }[]
}

export const getAssignmentScopesApi = async (
    userId: string,
    limit: number,
    offset: number
): Promise<Result<AssignmentScopeType, AxiosError>> => {
    try {
        const payload = { user_id: userId, limit, offset }
        sendAnalyticsTrack('TaskListScreen_Loading_Init', {
            user_id: userId,
        })

        const resp = await ExpertsClient.post<AssignmentScopeType>(
            '/v1/expert-view/home-page/get-assignment-scopes',
            payload
        )
        sendAnalyticsTrack('TaskListScreen_Loading_Success', {
            user_id: userId,
            count_of_steps: resp.data.scopes.length,
        })
        return ok(resp.data)
    } catch (e) {
        sendAnalyticsTrack('TaskListScreen_Loading_Failure', {
            user_id: userId,
        })
        return err(e as AxiosError)
    }
}

export const getAssignmentAtStepApi = async (
    userId: string,
    stepId: string,
    projectId: string
): Promise<Result<AssignmentAtStepType<ContainerType, unknown>, AxiosError>> => {
    try {
        const payload = {
            user_id: userId,
            step_id: stepId,
        }
        sendAnalyticsTrack('TaskScreen_Loading_Init', {
            user_id: userId,
            project_id: projectId,
            step_id: stepId,
        })
        const resp = await ExpertsClient.post<AssignmentAtStepType<ContainerType, unknown>>(
            '/v1/expert-view/task-tool/get-assignment-at-step',
            payload
        )
        sendAnalyticsTrack('TaskScreen_Loading_Success', {
            user_id: userId,
            project_id: resp.data.assignment.project_id,
            task_id: resp.data.assignment.task.id,
            step_id: resp.data.assignment.step?.id,
            is_reviewer: resp.data.assignment.step?.type === 'REVIEWER',
        })
        return ok(resp.data)
    } catch (e) {
        sendAnalyticsTrack('TaskScreen_Loading_Failure', {
            user_id: userId,
            project_id: projectId,
            step_id: stepId,
        })
        return err(e as AxiosError)
    }
}

export const getJobPreviewApi = async (
    jobId: string,
    stepId: string
): Promise<Result<AssignmentAtStepType<ContainerType, unknown>, AxiosError>> => {
    try {
        const payload = {
            job_id: jobId,
            step_id: stepId,
        }
        const resp = await ExpertsClient.post<AssignmentAtStepType<ContainerType, unknown>>(
            '/v1/internal-view/job-management/preview-job-as-task',
            payload
        )
        return ok(resp.data)
    } catch (e) {
        return err(e as AxiosError)
    }
}

export const getFeedbackPreviewApi = async (
    taskId: string,
    userId: string
): Promise<Result<AssignmentAtStepType<ContainerType, unknown>, AxiosError>> => {
    try {
        const payload = {
            task_id: taskId,
            user_id: userId,
        }
        const resp = await ExpertsClient.post<AssignmentAtStepType<ContainerType, unknown>>(
            '/v1/expert-view/get-task-feedback-preview',
            payload
        )
        return ok(resp.data)
    } catch (e) {
        return err(e as AxiosError)
    }
}

export const getTaskDetailsApi = async (
    taskId: string
): Promise<Result<AssignmentAtStepType<ContainerType, unknown>, AxiosError>> => {
    try {
        const payload = {
            task_id: taskId,
        }
        const resp = await ExpertsClient.post<AssignmentAtStepType<ContainerType, unknown>>(
            '/v1/internal-view/job-management/get-task-details',
            payload
        )
        return ok(resp.data)
    } catch (e) {
        return err(e as AxiosError)
    }
}

export const getUserTaskFeedbackApi = async (
    userId: string
): Promise<Result<UserTaskFeedback, AxiosError>> => {
    try {
        const payload = {
            user_id: userId,
        }
        const resp = await ExpertsClient.post<UserTaskFeedback>(
            'v1/expert-view/home-page/get-user-feedback',
            payload
        )
        return ok(resp.data)
    } catch (e) {
        return err(e as AxiosError)
    }
}

export const getTasksOutputByJobApi = async (
    jobId: string
): Promise<Result<ClientJobTasksOutputResponseDTO<ContainerType>, AxiosError>> => {
    try {
        const resp = await ExpertsClient.post<ClientJobTasksOutputResponseDTO<ContainerType>>(
            'v1/expert-view/get-detailed-feedback',
            {
                job_id: jobId,
            }
        )
        return ok(resp.data)
    } catch (e) {
        return err(e as AxiosError)
    }
}

export const getJobOutputsApi = async (
    jobId: string
): Promise<Result<ClientJobTasksOutputResponseDTO<ContainerType>, AxiosError>> => {
    try {
        const resp = await ExpertsClient.post<ClientJobTasksOutputResponseDTO<ContainerType>>(
            'v1/internal-view/job-management/get-job-outputs',
            {
                job_id: jobId,
            }
        )
        return ok(resp.data)
    } catch (e) {
        const error = e as ErrorResponseDTO
        if (error?.response?.data?.error?.code === ErrorCode.JOB_OUPUTS_NOT_POSSIBLE) {
            return ok({
                task_output: [],
            })
        }
        return err(e as AxiosError)
    }
}

export const getUserJobHistoryApi = async (payload: {
    limit: number
    offset: number
    jobId?: string
    taskId?: string
    workflowId?: string
    projectId?: string
}): Promise<Result<UserJobHistory, AxiosError>> => {
    try {
        const resp = await ExpertsClient.post<ClientUserJobHistoryResponseDTO>(
            'v1/internal-view/get-tasks-by-user',
            {
                limit: payload.limit,
                offset: payload.offset,
                job_id: payload.jobId,
                task_id: payload.taskId,
                workflow_id: payload.workflowId,
                project_id: payload.projectId,
            }
        )
        return ok({
            count: resp.data.count,
            jobs: resp.data.jobs_list.map(job => ({
                jobId: job.job_id,
                submittedAt: job.submitted_at,
                role: job.role,
                workflow_id: job.workflow_id,
            })),
        })
    } catch (e) {
        return err(e as AxiosError)
    }
}

export const getUserProjectHistoryApi = async (): Promise<
    Result<UserProjectHistoryResponseDTO, AxiosError>
> => {
    try {
        const resp = await ExpertsClient.post<ClientUserProjectHistoryResponseDTO>(
            'v1/internal-view/project-list-by-user'
        )
        return ok({
            projects: resp.data.project_workflows.map(project => ({
                projectId: project.project_id,
                projectName: project.project_name,
                workflows: project.workflows.map(workflow => ({
                    id: workflow.id,
                    name: workflow.name,
                })),
            })),
        })
    } catch (e) {
        return err(e as AxiosError)
    }
}

export const createSubmissionApi = async (
    assignmentId: string
): Promise<Result<CreateSubmissionType, AxiosError>> => {
    try {
        const payload = {
            assignment_id: assignmentId,
        }
        const resp = await ExpertsClient.post<CreateSubmissionType>('/v1/task-submissions', payload)
        return ok(resp.data)
    } catch (e) {
        return err(e as AxiosError)
    }
}

export const saveTaskApi = async (data: {
    userId: string
    submissionId: string
    timeElapsedInSeconds: number
    draftOutputContainer: ContainerDataType<ContainerType, unknown>
}): Promise<Result<void, AxiosError>> => {
    try {
        const payload = {
            user_id: data.userId,
            submission: {
                id: data.submissionId,
                time_elapsed_in_seconds: data.timeElapsedInSeconds,
                draft_output_container: data.draftOutputContainer,
            },
        }
        await ExpertsClient.post<void>('/v1/expert-view/task-tool/save-user-submission', payload)
        return ok(undefined)
    } catch (e) {
        return err(e as AxiosError)
    }
}

export const submitTaskApi = async (details: {
    userId: string
    submissionId: string
    timeElapsedInSeconds: number
    draftOutputContainer: ContainerDataType<ContainerType, unknown>
    pluginData: Record<string, unknown>
    feedbackData?: CSATFeedbackData
}): Promise<Result<void, AxiosError>> => {
    try {
        const payload = {
            user_id: details.userId,
            submission: {
                id: details.submissionId,
                time_elapsed_in_seconds: details.timeElapsedInSeconds,
                draft_output_container: details.draftOutputContainer,
                plugin_data: details.pluginData,
            },
            feedback_data: details.feedbackData,
        }
        await ExpertsClient.post<void>('/v1/expert-view/task-tool/submit-user-submission', payload)
        return ok(undefined)
    } catch (e) {
        return err(e as AxiosError)
    }
}

export const cancelTaskApi = async (
    id: string,
    message: string,
    skipReasons?: string[],
    feedbackData?: CSATFeedbackData
): Promise<Result<void, AxiosError>> => {
    try {
        const payload = {
            message: message,
            skip_reasons: skipReasons,
            feedback_data: feedbackData,
        }
        await ExpertsClient.post<void>(`/v1/task-submissions/${id}/cancel-by-user`, payload)
        return ok(undefined)
    } catch (e) {
        return err(e as AxiosError)
    }
}

export const getPluginsAssociationsAtStepApi = async (
    taskId: string
): Promise<Result<GetTaskLevelPluginConfigType, AxiosError>> => {
    try {
        const payload = { task_id: taskId }
        const resp = await ExpertsClient.post<GetTaskLevelPluginConfigType>(
            `/v1/expert-view/task-plugin-associations`,
            payload
        )
        return ok(resp.data)
    } catch (e) {
        return err(e as AxiosError)
    }
}

export const getSignedURLSApi = async (path: string): Promise<Result<string, AxiosError>> => {
    try {
        const resp = await LuigiClient.get(`api/v1/attachments?url=${path}&needsAuth=true`)
        return ok(resp.request.responseURL)
    } catch (e) {
        return err(e as AxiosError)
    }
}

export const getSignedURLSByPathAPI = async (
    taskId: string,
    fileURL: string
): Promise<Result<FileUploadApiResponseType, AxiosError>> => {
    try {
        const resp = await ExpertsClient.post(`v1/expert-view/get-signed-urls`, {
            task_id: taskId,
            paths: [fileURL],
        })
        return ok(resp.data)
    } catch (e) {
        return err(e as AxiosError)
    }
}

export const uploadFileAPI = async (
    file: File,
    uploadUrl: string
): Promise<Result<unknown, AxiosError>> => {
    try {
        const resp = await S3Client.put(uploadUrl, file)
        return ok(resp.data)
    } catch (e) {
        return err(e as AxiosError)
    }
}

export const getFileDownloadUrlApi = async (
    path: string
): Promise<Result<GetFileDownloadUrlResponseDto, AxiosError>> => {
    try {
        const resp = await ExpertsClient.post(`v1/attributes/files`, {
            paths: [path],
        })
        return ok(resp.data)
    } catch (e) {
        return err(e as AxiosError)
    }
}

export const getOnHoldTaskDetailsApi = async (payload: {
    user_id: string
    step_id: string
}): Promise<Result<GetOnHoldTaskDetailsResponseDTO, AxiosError>> => {
    try {
        const resp = await ExpertsClient.post(`v1/expert-view/get-on-hold-task-details`, payload)
        return ok(resp.data)
    } catch (e) {
        return err(e as AxiosError)
    }
}

export const resumeTaskByUserApi = async (details: {
    id: string
    user_id: string
}): Promise<Result<void, AxiosError>> => {
    const payload = {
        user_id: details.user_id,
    }
    try {
        await ExpertsClient.post(`/v1/task-submissions/${details.id}/resume-by-user`, payload)
        return ok(undefined)
    } catch (e) {
        return err(e as AxiosError)
    }
}

export const holdTaskByUserApi = async (details: {
    userId: string
    submissionId: string
    timeElapsedInSeconds: number
    draftOutputContainer: ContainerDataType<ContainerType, unknown>
    message?: string
}): Promise<Result<void, AxiosError>> => {
    const payload = {
        user_id: details.userId,
        submission: {
            id: details.submissionId,
            time_elapsed_in_seconds: details.timeElapsedInSeconds,
            draft_output_container: details.draftOutputContainer,
        },
        message: details.message,
    }
    try {
        await ExpertsClient.post(`/v1/expert-view/${payload.submission.id}/hold-by-user`, payload)
        return ok(undefined)
    } catch (e) {
        return err(e as AxiosError)
    }
}

export const getPreviewTaskForExpertApi = async (
    taskId: string,
    userId: string
): Promise<Result<AssignmentAtStepType<ContainerType, unknown>, AxiosError>> => {
    try {
        const resp = await ExpertsClient.post('v1/expert-view/get-preview-task', {
            task_id: taskId,
            user_id: userId,
        })
        return ok(resp.data)
    } catch (e) {
        return err(e as AxiosError)
    }
}

export const getPreviewTaskApi = async (
    taskId: string
): Promise<Result<AssignmentAtStepType<ContainerType, unknown>, AxiosError>> => {
    try {
        const resp = await ExpertsClient.post('v1/internal-view/get-preview-task', {
            task_id: taskId,
        })
        return ok(resp.data)
    } catch (e) {
        return err(e as AxiosError)
    }
}

export const getStepLevelTaskDetailsApi = async (
    userId: string,
    stepId: string
): Promise<Result<StepLevelTaskDetailsType, AxiosError>> => {
    try {
        const payload = {
            user_id: userId,
            step_id: stepId,
        }
        const resp = await ExpertsClient.post(
            '/v1/expert-view/home-page/get-assignment-scope-step-details',
            payload
        )
        return ok(resp.data)
    } catch (e) {
        return err(e as AxiosError)
    }
}

export const submitTurnDataApi = async (
    payload: SubmitTurnDataPayload
): Promise<Result<SubmissionPlayTurnAPIResponseType, AxiosError>> => {
    try {
        const resp = await ExpertsClient.post('v1/expert-view/task-tool/submission-play-turn', {
            user_id: payload.userId,
            is_non_meta: true,
            submission: {
                id: payload.submissionId,
                time_elapsed_in_seconds: payload.timeElapsedInSeconds,
                turn_data: payload.data,
                supplement: payload.supplement,
                extra_info: payload.extraInfo,
                turn_annotations: payload.turnAnnotations,
                edit_prompt_index: payload.promptIndex,
                plugin_data: payload.pluginData,
            },
        })
        return ok(resp.data)
    } catch (e) {
        return err(e as AxiosError)
    }
}

export const submitTurnDataStreamingApi = async (
    payload: SubmitTurnDataPayload
): Promise<Result<ReadableStreamDefaultReader<Uint8Array>, Error>> => {
    try {
        const finalPayload = {
            user_id: payload.userId,
            is_non_meta: true,
            submission: {
                id: payload.submissionId,
                time_elapsed_in_seconds: payload.timeElapsedInSeconds,
                turn_data: payload.data,
                supplement: payload.supplement,
                extra_info: payload.extraInfo,
                turn_annotations: payload.turnAnnotations,
                edit_prompt_index: payload.promptIndex,
                plugin_data: payload.pluginData,
            },
        }

        const token = getBearerToken()
        const response = await fetch(
            `${appConfig.expertsApiBaseUrl}v1/expert-view/task-tool/submission-play-turn`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Caller-Service': 'experts-client',
                    ...(token ? { Authorization: token } : {}),
                },
                body: JSON.stringify(finalPayload),
            }
        )

        if (!response.ok || !response.body) {
            throw new Error(`Network error: ${response.status} ${response.statusText}`)
        }

        const reader = response.body.getReader()
        return ok(reader)
    } catch (e) {
        return err(e as Error)
    }
}

export const fillDraftDataApi = async (
    userId: string,
    submissionId: string,
    mainInfo: ConversationMainInfoType | Conversationsv2MainInfoType,
    type: ConversationalTurnTypes.DraftFill | Conversationalv2TurnTypes.DraftFill
): Promise<Result<void, AxiosError>> => {
    try {
        const resp = await ExpertsClient.post('v1/expert-view/task-tool/submission-play-turn', {
            user_id: userId,
            is_non_meta: true,
            submission: {
                id: submissionId,
                time_elapsed_in_seconds: 1, // Can't be 0 as it'll fail so we can keep it hardcoded as 1 as it'll not be used anyways
                turn_data: {
                    type,
                    data: {
                        draft_data: mainInfo.value,
                    },
                },
            },
        })
        return ok(resp.data)
    } catch (e) {
        return err(e as AxiosError)
    }
}

export const getOnHoldAssignmentApi = async (
    userId: string
): Promise<Result<OnHoldAssignmentScopeResponseType, AxiosError>> => {
    try {
        const payload = { user_id: userId }
        sendAnalyticsTrack('TaskSavedForLater_ListScreen_Loading_Init', {
            user_id: userId,
        })

        const resp = await ExpertsClient.post<OnHoldAssignmentScopeResponseType>(
            '/v1/expert-view/home-page/get-on-hold-assignment-scopes ',
            payload
        )
        sendAnalyticsTrack('TaskSavedForLater_ListScreen_Loading_Success', {
            user_id: userId,
            count_of_steps: resp.data.scopes.length,
        })
        return ok(resp.data)
    } catch (e) {
        sendAnalyticsTrack('TaskSavedForLater_ListScreen_Loading_Failure', {
            user_id: userId,
        })
        return err(e as AxiosError)
    }
}

export const getJobHierarchyApi = async (
    jobId: string
): Promise<Result<JobHierarchy, AxiosError>> => {
    try {
        const resp = await ExpertsClient.post<JobHierarchy>(
            `v1/internal-view/job-management/fetch-job-hierarchy`,
            {
                job_id: jobId,
            }
        )
        return ok(resp.data)
    } catch (e) {
        return err(e as AxiosError)
    }
}

export const getWorkflowStepsByJobIdApi = async (
    jobId: string
): Promise<
    Result<
        {
            step_details: StepType[]
        },
        AxiosError
    >
> => {
    try {
        const resp = await ExpertsClient.post<{
            step_details: StepType[]
        }>(`v1/internal-view/job-management/fetch-job-steps`, {
            job_id: jobId,
        })
        return ok(resp.data)
    } catch (e) {
        return err(e as AxiosError)
    }
}

export const testToolCallApi = async (
    jobId: string,
    inputParams: JSONObject
): Promise<Result<ToolCallResponseType, AxiosError>> => {
    try {
        const resp = await ExpertsClient.post(`v1/expert-view/test-mcp-tool`, {
            job_id: jobId,
            input_params: inputParams,
        })
        return ok(resp.data)
    } catch (e) {
        return err(e as AxiosError)
    }
}
